import {
  CallsResponseDto,
  GenericGetFilterDto,
  PagedResponse,
  CaseRequestDto,
  CaseResponseDto,
  CaseHistoryResponseDto,
} from '@bandido/shared/dtos';
import { AxiosError, AxiosResponse } from 'axios';
import { useMutation, UseMutationOptions, useQuery } from 'react-query';
import { QueryKeys } from '../common/types';
import { client } from '../common/services';
import { OriginatedByChannel } from '@bandido/shared/types';

interface useGetCallsProps extends GenericGetFilterDto {
  onErrorCallback?: (_e: AxiosError) => void;
}

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const getValidCallRouteId = (id?: CallsResponseDto['id'] | null) => {
  if (typeof id !== 'string') return null;

  const normalizedId = id.trim();

  if (!normalizedId || normalizedId === 'undefined' || normalizedId === 'null') return null;

  return UUID_PATTERN.test(normalizedId) ? normalizedId : null;
};

export const useGetCalls = (params?: useGetCallsProps | undefined) => {
  const { currentPage = 1, pageSize = 0, search, onErrorCallback } = params || {};
  return useQuery({
    queryKey: [QueryKeys.getCalls, currentPage, pageSize, search],
    queryFn: async (): Promise<PagedResponse<CallsResponseDto> | undefined> => {
      try {
        const result: AxiosResponse<PagedResponse<CallsResponseDto>, any> = await client.get('calls', {
          params: {
            currentPage,
            pageSize,
            search,
          },
        });

        return result.data;
      } catch (e) {
        onErrorCallback && onErrorCallback(e as AxiosError);
      }
    },
    refetchOnWindowFocus: true,
  });
};

export const useAssignCallToCase = (
  callId?: CallsResponseDto['id'] | null,
  options?:
    | Omit<
        UseMutationOptions<
          AxiosResponse<CaseHistoryResponseDto, any>,
          unknown,
          { caseId: CaseResponseDto['id'] },
          unknown
        >,
        'mutationFn'
      >
    | undefined,
) =>
  useMutation((data) => {
    const routeCallId = getValidCallRouteId(callId);

    if (!routeCallId) throw new Error('Missing call route id');

    return client.post(`case/${data.caseId}/calls/${routeCallId}`);
  }, options);

export const useCreateCaseForCall = (
  id?: CallsResponseDto['id'] | null,
  options?:
    | Omit<UseMutationOptions<AxiosResponse<number, any>, unknown, CaseRequestDto, unknown>, 'mutationFn'>
    | undefined,
) =>
  useMutation((data) => {
    const routeCallId = getValidCallRouteId(id);

    if (!routeCallId) throw new Error('Missing call route id');

    return client.post(`calls/${routeCallId}/case`, { ...data, originatedByChannel: OriginatedByChannel.CALL });
  }, options);
